// FilterToolbar.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    IconButton,
    TextField,
    Button,
    Chip,
    useMediaQuery,
    Box,
    Tooltip,
    Toolbar,
    FormControlLabel,
    Switch,
    Paper,
    InputAdornment,
} from '@mui/material';
import {
    FilterList as FilterListIcon,
    Refresh as RefreshIcon,
    ArrowDropDown as ArrowDropDownIcon,
    Build as BuildIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import {
    FaFilePdf,
    FaCut,
    FaDrawPolygon,
    FaTrash,
    FaBuffer,
    FaUndo,
    FaArrowsAltH,
    FaRuler, // <-- medición
} from 'react-icons/fa';
import Autocomplete from '@mui/material/Autocomplete';
import { VariableSizeList } from 'react-window';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import 'moment/locale/es';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ResetButton, FilterBar, StyledDivider, ToolsMenu, ToolButton } from './FilterToolbarStyle';

const LISTBOX_PADDING = 8;

const renderRow = ({ data, index, style }) => {
    const option = data[index];
    const title = option?.props?.children ?? '';
    return (
        <Tooltip title={title} key={index}>
            {React.cloneElement(option, {
                style: {
                    ...style,
                    top: style.top + LISTBOX_PADDING,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px',
                },
            })}
        </Tooltip>
    );
};

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef(function OuterElementType(props, ref) {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = useCallback(
        (child) => {
            // Mantener compatibilidad sin depender de ListSubheader importado
            if (React.isValidElement(child) && child?.type?.muiName === 'ListSubheader') return 48;
            return itemSize;
        },
        [itemSize]
    );

    const getHeight = useCallback(() => {
        if (itemCount > 8) return 8 * itemSize;
        return itemData.reduce((a, b) => a + getChildSize(b), 0);
    }, [itemCount, itemData, getChildSize, itemSize]);

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

// --- Detalle de herramientas (incluye medición) ---
const toolDetails = {
    cut: { label: 'Cortar línea', icon: <FaCut /> },
    draw: { label: 'Dibujar línea', icon: <FaDrawPolygon /> },
    delete: { label: 'Borrar líneas', icon: <FaTrash /> },
    buffer: { label: 'Buffer de línea', icon: <FaBuffer /> },
    stretch: { label: 'Estirar línea', icon: <FaArrowsAltH /> },
    measurement: { label: 'Medición', icon: <FaRuler /> },
};

const normalize = (s) =>
    String(s ?? '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');

// -------------------------
// ✅ Helpers: derivar opciones desde FeatureCollection (SOLO NO dashboard)
// -------------------------
const toStr = (v) => (v === null || v === undefined ? '' : String(v).trim());

const normalizeToFeaturesArray = (maybeGeojson) => {
    if (!maybeGeojson) return [];

    if (maybeGeojson?.data?.features && Array.isArray(maybeGeojson.data.features)) {
        return maybeGeojson.data.features;
    }

    if (maybeGeojson?.features && Array.isArray(maybeGeojson.features)) {
        return maybeGeojson.features;
    }

    if (Array.isArray(maybeGeojson)) return maybeGeojson;

    return [];
};

const uniqueSorted = (arr) => {
    const clean = (arr || [])
        .map((x) => toStr(x))
        .filter((x) => x !== '');

    const set = new Set(clean);
    const out = Array.from(set);

    const allNumeric = out.every((x) => /^-?\d+(\.\d+)?$/.test(String(x)));
    if (allNumeric) out.sort((a, b) => Number(a) - Number(b));
    else out.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));

    return out;
};

const normalizeKey = (k) => normalize(String(k ?? '')).replace(/[^a-z0-9]/g, '');

const getFirstProp = (propsObj, keys) => {
    if (!propsObj) return '';

    for (const k of keys) {
        if (Object.prototype.hasOwnProperty.call(propsObj, k)) {
            const s = toStr(propsObj[k]);
            if (s) return s;
        }
    }

    const map = {};
    Object.keys(propsObj).forEach((k) => {
        map[normalizeKey(k)] = k;
    });

    for (const k of keys) {
        const realKey = map[normalizeKey(k)];
        if (realKey && Object.prototype.hasOwnProperty.call(propsObj, realKey)) {
            const s = toStr(propsObj[realKey]);
            if (s) return s;
        }
    }

    return '';
};

const buildOptionsFromLotesFeatures = (features) => {
    const loteIds = [];
    const fincaNames = [];
    const fincaCodes = [];
    const lotes = [];

    for (const f of features || []) {
        const p = f?.properties || {};

        const loteId = getFirstProp(p, ['_id', 'id', 'ID', 'LOTE_ID', 'LOTEID', 'LoteID', 'loteId']);


        const fincaName = getFirstProp(p, [
            'N. FINCA',
            'N FINCA',
            'N_FINCA',
            'NFINCA',
            'Nombre Fin',
            'NOMBRE FIN',
            'NOMBRE_FIN',
            'Nombre Finca',
            'NOMBRE_FINCA',
            'NOMBRE FINCA',
            'FINCA_NOMBRE',
            'FINCA_NAME',
        ]);

        const fincaCode = getFirstProp(p, [
            'C. FINCA',
            'C FINCA',
            'C_FINCA',
            'CFINCA',
            'FINCA',
            'finca',
            'CODIGO_FINCA',
            'COD_FINCA',
            'FINCA_CODIGO',
            'CODIGO DE FINCA',
        ]);



        const lote = getFirstProp(p, ['LOTE', 'lote', 'Lote', 'NUM_LOTE', 'LOTE_NUM']);

        if (loteId) loteIds.push(loteId);
        pushVals(fincaNames, fincaName);
        pushVals(fincaCodes, fincaCode);
        if (lote) lotes.push(lote);
    }

    return {
        loteIdOptions: uniqueSorted(loteIds),
        fincaNameOptions: uniqueSorted(fincaNames),
        codigoFincaOptions: uniqueSorted(fincaCodes),
        loteOptions: uniqueSorted(lotes),
    };
};
const pushVals = (arr, v) => {
    if (v == null) return;
    if (Array.isArray(v)) {
        v.forEach(x => {
            const s = toStr(x);
            if (s) arr.push(s);
        });
        return;
    }
    const s = toStr(v);
    if (s) arr.push(s);
};

// -------------------------
// ✅ Buscador interno dentro del dropdown (normaliza + tokens)
// -------------------------
const normalizeSearchText = (s) =>
    String(s ?? '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');

const filterStringOptions = (options, query) => {
    const q = normalizeSearchText(query);
    if (!q) return options;

    const tokens = q.split(' ').filter(Boolean);
    if (!tokens.length) return options;

    return (options || []).filter((opt) => {
        const val = normalizeSearchText(opt);
        return tokens.every((t) => val.includes(t));
    });
};

// -------------------------
// ✅ Limpia payload (solo NO dashboard): no null/undefined/''
// (Dedup por campo, evita duplicados por UI)
// -------------------------
const buildCleanFilters = (obj) => {
    const out = {};

    Object.entries(obj || {}).forEach(([k, v]) => {
        if (v === null || v === undefined) return;

        if (Array.isArray(v)) {
            const arr = v.map((x) => toStr(x)).filter((x) => x !== '');
            if (arr.length === 0) return;
            out[k] = Array.from(new Set(arr));
            return;
        }

        const s = toStr(v);
        if (!s) return;
        out[k] = v;
    });

    return out;
};

const FilterToolbar = ({
                           isSidebarOpen,
                           isDashboardIndicators,
                           filterOptions = [],
                           onDateChange: onDateChangeProp,
                           handleDateChange: handleDateChangeProp,
                           onFilterClick,

                           loteOptions = [],
                           operadorOptions = [],
                           fincasOptions = [],
                           zafrasOptions = [],
                           tercioOptions = [],

                           productosOptionsAA = [],
                           operadorOptionsAA = [],
                           fincasOptionsAA = [],
                           zafrasOptionsAA = [],
                           loteOptionsAA = [],
                           tercioOptionsAA = [],

                           operadorOptionsFR = [],
                           fincasOptionsFR = [],
                           productosOptionsFR = [],
                           zafraOptionsFR = [],
                           tercioOptionsFR = [],

                           loteOptionsDM = [],
                           fincasOptionsDM = [],
                           zafraOptionsDM = [],
                           tercioOptionsDM = [],

                           dateRange,
                           selectedActivity,
                           selectedAnalysisType,

                           onGenerateReport,
                           isGeneratingReport,

                           handleCutLine,
                           handleDrawLine,
                           handleDeleteLine,
                           handleToggleBuffer,
                           handleUndo,
                           handleStretchLine,
                           handleMeasurement,
                           activeTool,
                           isBufferActive,
                           bufferValue,
                           setBufferValue,
                           showUnfilteredLines,
                           toggleUnfilteredLines,

                           lotesGeojson,
                           setLoteFilters,
                           ...restProps
                       }) => {
    moment.locale('es');

    const onDateChange = handleDateChangeProp || onDateChangeProp;

    const startOfMonth = useMemo(() => moment().startOf('month'), []);
    const endOfMonth = useMemo(() => moment().endOf('month'), []);

    const [startDate, setStartDate] = useState(dateRange?.startDate || startOfMonth);
    const [endDate, setEndDate] = useState(dateRange?.endDate || endOfMonth);

    // ✅ Forzar reset completo de los Autocomplete NO-dashboard (incluye inputValue interno)
    const [simpleResetNonce, setSimpleResetNonce] = useState(0);

    // -------------------------
    // ✅ Estado buscador interno por select (dropdown)
    // -------------------------
    const dropdownSearchRef = useRef({});
    const [dropdownSearch, setDropdownSearch] = useState({}); // (se conserva por si lo usas en debug/UI)

    const setDropdownSearchValue = useCallback((key, value) => {
        setDropdownSearch((prev) => {
            const next = { ...(prev || {}), [key]: value };
            dropdownSearchRef.current = next;
            return next;
        });
    }, []);

    const clearDropdownSearchValue = useCallback((key) => {
        setDropdownSearch((prev) => {
            if (!prev || !Object.prototype.hasOwnProperty.call(prev, key)) return prev;
            const next = { ...prev };
            delete next[key];
            dropdownSearchRef.current = next;
            return next;
        });
    }, []);

    // -------------------------
    // ✅ Control de apertura por select
    // -------------------------
    const openByKeyRef = useRef({});
    const [openByKey, setOpenByKey] = useState({});

    const setOpenForKey = useCallback((key, isOpen) => {
        setOpenByKey((prev) => {
            if (!isOpen) {
                const next = { ...(prev || {}) };
                delete next[key];
                openByKeyRef.current = next;
                return next;
            }
            const next = { [key]: true }; // cierra los demás
            openByKeyRef.current = next;
            return next;
        });
    }, []);

    const closeForKey = useCallback((key) => setOpenForKey(key, false), [setOpenForKey]);

    // ✅ refs para detectar interacciones dentro del dropdown
    const paperRefs = useRef({});
    const ignoreCloseRef = useRef({});
    const fieldRootRefs = useRef({});
    const anchorRefs = useRef({});

    // -------------------------
    // DashboardIndicators: estado ORIGINAL (no se toca)
    // -------------------------
    const [selectedFilters, setSelectedFilters] = useState({
        loteCM: null,
        operadorCM: null,
        tipoAeronave: null,
        zafraCM: null,
        tercioCM: null,
        fincaCM: null,
        operadorAA: null,
        fincaAA: null,
        productoAA: null,
        zafraAA: null,
        loteAA: null,
        tercioAA: null,
        loteDM: null,
        fincaDM: null,
        zafraDM: null,
        tercioDM: null,
        operadorFR: null,
        fincaFR: null,
        productoFR: null,
        zafraFR: null,
        tercioFR: null,
    });

    // -------------------------
    // NO DashboardIndicators: SOLO 4 filtros (multi-select)
    // -------------------------
    const simpleFiltersConfig = useMemo(() => {
        return [
            { value: 'loteId', label: 'ID', options: [] },
            { value: 'finca', label: 'N. FINCA', options: [] },
            { value: 'codigoFinca', label: 'C. FINCA', options: [] },
            { value: 'lote', label: 'LOTE', options: [] },
        ];
    }, []);

    const buildSimpleSelected = useCallback((cfg) => {
        const obj = {};
        (cfg || []).forEach((f) => {
            obj[f.value] = [];
        });
        return obj;
    }, []);

    const [selectedSimpleFilters, setSelectedSimpleFilters] = useState(() =>
        buildSimpleSelected(simpleFiltersConfig)
    );

    useEffect(() => {
        setSelectedFilters({
            loteCM: null,
            operadorCM: null,
            tipoAeronave: null,
            zafraCM: null,
            tercioCM: null,
            fincaCM: null,
            operadorAA: null,
            fincaAA: null,
            productoAA: null,
            zafraAA: null,
            loteAA: null,
            tercioAA: null,
            operadorFR: null,
            fincaFR: null,
            productoFR: null,
            zafraFR: null,
            tercioFR: null,
            loteDM: null,
            fincaDM: null,
            zafraDM: null,
            tercioDM: null,
        });

        setStartDate(dateRange?.startDate || startOfMonth);
        setEndDate(dateRange?.endDate || endOfMonth);

        setSelectedSimpleFilters(buildSimpleSelected(simpleFiltersConfig));

        dropdownSearchRef.current = {};
        setDropdownSearch({});
        openByKeyRef.current = {};
        setOpenByKey({});
    }, [selectedActivity, dateRange, startOfMonth, endOfMonth, simpleFiltersConfig, buildSimpleSelected]);

    const handleStartDateChange = useCallback(
        (date) => {
            if (date && date.isValid() && moment(date).isSameOrBefore(endDate, 'day')) {
                setStartDate(date);
                onDateChange?.({ startDate: date, endDate });
            }
        },
        [endDate, onDateChange]
    );

    const handleEndDateChange = useCallback(
        (date) => {
            if (date && date.isValid() && moment(date).isSameOrAfter(startDate, 'day')) {
                setEndDate(date);
                onDateChange?.({ startDate, endDate: date });
            }
        },
        [startDate, onDateChange]
    );

    const handleFilterChange = useCallback((filterName, value) => {
        setSelectedFilters((prev) => ({ ...prev, [filterName]: value }));
    }, []);

    const handleSimpleFilterChange = useCallback(
        (filterName, value) => {
            const nextValue = Array.isArray(value) ? value : value ? [value] : [];

            setSelectedSimpleFilters((prev) => ({ ...prev, [filterName]: nextValue }));

            setLoteFilters?.((prev) => ({
                ...(prev || { loteId: [], finca: [], codigoFinca: [], lote: [] }),
                [filterName]: nextValue,
            }));
        },
        [setLoteFilters]
    );

    const handleReset = useCallback(() => {
        setStartDate(startOfMonth);
        setEndDate(endOfMonth);

        if (isDashboardIndicators) {
            setSelectedFilters({
                loteCM: null,
                operadorCM: null,
                tipoAeronave: null,
                zafraCM: null,
                tercioCM: null,
                fincaCM: null,
                operadorAA: null,
                fincaAA: null,
                productoAA: null,
                zafraAA: null,
                loteAA: null,
                tercioAA: null,
                operadorFR: null,
                fincaFR: null,
                productoFR: null,
                zafraFR: null,
                tercioFR: null,
                loteDM: null,
                fincaDM: null,
                zafraDM: null,
                tercioDM: null,
            });
        } else {
            setSelectedSimpleFilters(buildSimpleSelected(simpleFiltersConfig));
            setLoteFilters?.({ loteId: [], finca: [], codigoFinca: [], lote: [] });

            // ✅ Fuerza remount de Autocomplete (limpia texto escrito en el input)
            setSimpleResetNonce((n) => n + 1);

            if (typeof onFilterClick === 'function') {
                onFilterClick({ loteId: [], finca: [], codigoFinca: [], lote: [] });
            }
        }

        dropdownSearchRef.current = {};
        setDropdownSearch({});
        openByKeyRef.current = {};
        setOpenByKey({});
        onDateChange?.({ startDate: startOfMonth, endDate: endOfMonth });
    }, [
        isDashboardIndicators,
        onDateChange,
        startOfMonth,
        endOfMonth,
        simpleFiltersConfig,
        buildSimpleSelected,
        setLoteFilters,
        onFilterClick,
    ]);

    const handleClearSimpleFilters = useCallback(() => {
        if (isDashboardIndicators) return;

        setSelectedSimpleFilters(buildSimpleSelected(simpleFiltersConfig));
        setLoteFilters?.({ loteId: [], finca: [], codigoFinca: [], lote: [] });

        // ✅ Fuerza remount de Autocomplete (limpia texto escrito en el input)
        setSimpleResetNonce((n) => n + 1);

        if (typeof onFilterClick === 'function') {
            onFilterClick({ loteId: [], finca: [], codigoFinca: [], lote: [] });
        }

        dropdownSearchRef.current = {};
        setDropdownSearch({});
        openByKeyRef.current = {};
        setOpenByKey({});
    }, [isDashboardIndicators, buildSimpleSelected, simpleFiltersConfig, setLoteFilters, onFilterClick]);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // UI/UX: compacta chips para multi-select
    const renderCompactTags = useCallback(
        (value, getTagProps) => {
            if (!Array.isArray(value) || value.length === 0) return null;

            const chipMaxWidth = isSmallScreen ? 60 : 90;

            const first = value[0];
            const extra = value.length - 1;
            const firstTagProps = getTagProps({ index: 0 });

            const allText = value.join(', ');

            return (
                <>
                    <Tooltip title={String(first)} arrow>
                        <Box component="span">
                            <Chip
                                {...firstTagProps}
                                size="small"
                                label={String(first)}
                                sx={{
                                    maxWidth: chipMaxWidth,
                                    height: 22,
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    },
                                }}
                            />
                        </Box>
                    </Tooltip>

                    {extra > 0 && (
                        <Tooltip title={allText} arrow>
                            <Box component="span">
                                <Chip
                                    size="small"
                                    label={`+${extra}`}
                                    sx={{
                                        ml: 0.5,
                                        height: 22,
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    )}
                </>
            );
        },
        [isSmallScreen]
    );

    const handleFilterButtonClick = useCallback(() => {
        if (isDashboardIndicators) {
            onFilterClick?.(selectedFilters);
        } else {
            onFilterClick?.(buildCleanFilters(selectedSimpleFilters));
        }
    }, [isDashboardIndicators, onFilterClick, selectedFilters, selectedSimpleFilters]);

    // Dashboard: dynamicFilters
    const dynamicFilters = useMemo(() => {
        return filterOptions.filter((filter) => filter.value !== 'fechaInicio' && filter.value !== 'fechaFin');
    }, [filterOptions]);

    const getOptionsForFilter = useCallback(
        (filterValue) => {
            switch (filterValue) {
                case 'tipoAeronave':
                    return {
                        options: filterOptions.find((f) => f.value === 'tipoAeronave')?.options || [],
                        loading: false,
                    };
                case 'loteCM':
                    return { options: loteOptions, loading: false };
                case 'operadorCM':
                    return { options: operadorOptions, loading: false };
                case 'fincaCM':
                    return { options: fincasOptions, loading: false };
                case 'zafraCM':
                    return { options: zafrasOptions, loading: false };
                case 'tercioCM':
                    return { options: tercioOptions, loading: false };

                case 'operadorAA':
                    return { options: operadorOptionsAA, loading: false };
                case 'fincaAA':
                    return { options: fincasOptionsAA, loading: false };
                case 'zafraAA':
                    return { options: zafrasOptionsAA, loading: false };
                case 'loteAA':
                    return { options: loteOptionsAA, loading: false };
                case 'tercioAA':
                    return { options: tercioOptionsAA, loading: false };
                case 'productoAA':
                    return { options: productosOptionsAA, loading: false };

                case 'operadorFR':
                    return { options: operadorOptionsFR, loading: false };
                case 'fincaFR':
                    return { options: fincasOptionsFR, loading: false };
                case 'productoFR':
                    return { options: productosOptionsFR, loading: false };
                case 'zafraFR':
                    return { options: zafraOptionsFR, loading: false };
                case 'tercioFR':
                    return { options: tercioOptionsFR, loading: false };

                case 'loteDM':
                    return { options: loteOptionsDM, loading: false };
                case 'fincaDM':
                    return { options: fincasOptionsDM, loading: false };
                case 'zafraDM':
                    return { options: zafraOptionsDM, loading: false };
                case 'tercioDM':
                    return { options: tercioOptionsDM, loading: false };

                default:
                    return { options: [], loading: false };
            }
        },
        [
            filterOptions,
            loteOptions,
            operadorOptions,
            fincasOptions,
            zafrasOptions,
            tercioOptions,
            operadorOptionsAA,
            fincasOptionsAA,
            zafrasOptionsAA,
            loteOptionsAA,
            tercioOptionsAA,
            productosOptionsAA,
            operadorOptionsFR,
            fincasOptionsFR,
            productosOptionsFR,
            zafraOptionsFR,
            tercioOptionsFR,
            loteOptionsDM,
            fincasOptionsDM,
            zafraOptionsDM,
            tercioOptionsDM,
        ]
    );

    // -------------------------
    // ✅ Derivar opciones desde FeatureCollection (SOLO NO dashboard)
    // -------------------------
    const lotesGeojsonSource = useMemo(() => {
        return (
            lotesGeojson ||
            restProps?.lotesGeoJSON ||
            restProps?.lotesFeatureCollection ||
            restProps?.polygonsGeojson ||
            restProps?.polygons ||
            restProps?.geoJson ||
            restProps?.geojson ||
            restProps?.lotes ||
            null
        );
    }, [lotesGeojson, restProps]);

    const lotesFeatures = useMemo(() => {
        if (isDashboardIndicators) return [];
        return normalizeToFeaturesArray(lotesGeojsonSource);
    }, [isDashboardIndicators, lotesGeojsonSource]);

    const derivedLotesOptions = useMemo(() => {
        if (isDashboardIndicators) {
            return { loteIdOptions: [], fincaNameOptions: [], codigoFincaOptions: [], loteOptions: [] };
        }
        return buildOptionsFromLotesFeatures(lotesFeatures);
    }, [isDashboardIndicators, lotesFeatures]);

    const allFincas = useMemo(
        () =>
            Array.from(
                new Set(
                    []
                        .concat(fincasOptions || [])
                        .concat(fincasOptionsAA || [])
                        .concat(fincasOptionsFR || [])
                        .concat(fincasOptionsDM || [])
                        .filter(Boolean)
                )
            ),
        [fincasOptions, fincasOptionsAA, fincasOptionsFR, fincasOptionsDM]
    );

    const allLotes = useMemo(
        () =>
            Array.from(
                new Set([].concat(loteOptions || []).concat(loteOptionsAA || []).concat(loteOptionsDM || []).filter(Boolean))
            ),
        [loteOptions, loteOptionsAA, loteOptionsDM]
    );

    const getOptionsForSimpleFilter = useCallback(
        (filter) => {
            if (Array.isArray(filter?.options) && filter.options.length > 0) {
                return { options: uniqueSorted(filter.options), loading: false };
            }

            if (!isDashboardIndicators) {
                const v = String(filter?.value || '').trim();
                if (v === 'loteId') {
                    const opts = derivedLotesOptions.loteIdOptions || [];
                    if (opts.length > 0) return { options: opts, loading: false };
                }
                if (v === 'finca') {
                    const opts = derivedLotesOptions.fincaNameOptions || [];
                    if (opts.length > 0) return { options: opts, loading: false };
                }
                if (v === 'codigoFinca') {
                    const opts = derivedLotesOptions.codigoFincaOptions || [];
                    if (opts.length > 0) return { options: opts, loading: false };
                }
                if (v === 'lote') {
                    const opts = derivedLotesOptions.loteOptions || [];
                    if (opts.length > 0) return { options: opts, loading: false };
                }
            }

            const fromSwitch = getOptionsForFilter(filter.value);
            if (Array.isArray(fromSwitch?.options) && fromSwitch.options.length > 0) {
                return { options: uniqueSorted(fromSwitch.options), loading: false };
            }

            const lab = normalize(filter?.label);
            if (lab === normalize('Finca')) return { options: uniqueSorted(allFincas), loading: false };
            if (lab === normalize('Lote') || lab === normalize('Lote ID')) return { options: uniqueSorted(allLotes), loading: false };

            return { options: [], loading: false };
        },
        [getOptionsForFilter, allFincas, allLotes, derivedLotesOptions, isDashboardIndicators]
    );

    const isAerialApplications = selectedAnalysisType === 'APLICACIONES_AEREAS';
    const isCosechaMecanica = selectedAnalysisType === 'COSECHA_MECANICA';
    const isDeteccionMaleza = selectedAnalysisType === 'DETECCION_MALEZA';

    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const [localBuffer, setLocalBuffer] = useState(() => {
        const n = Number(bufferValue);
        return Number.isFinite(n) && n > 0 ? String(n) : '';
    });

    useEffect(() => {
        if (!isBufferActive) return;
        const n = Number(bufferValue);
        setLocalBuffer(Number.isFinite(n) && n > 0 ? String(n) : '');
    }, [bufferValue, isBufferActive]);

    /**
     * ✅ ya NO guardamos buffer en localStorage.
     * ✅ cuando se limpia/invalid, lo dejamos en 0.
     */
    const commitBufferValue = useCallback(
        (raw, opts = { syncText: true }) => {
            const cleaned = String(raw ?? '').replace(',', '.').trim();

            if (!cleaned) {
                setBufferValue(0);
                if (opts?.syncText) setLocalBuffer('');
                return;
            }

            const n = Number(cleaned);
            if (!Number.isFinite(n) || n <= 0) {
                setBufferValue(0);
                if (opts?.syncText) setLocalBuffer('');
                return;
            }

            const safe = Math.max(0.01, Math.min(n, 1000));
            const formatted = Number.isInteger(safe) ? String(safe) : String(Number(safe.toFixed(2)));

            setBufferValue(safe);
            if (opts?.syncText) setLocalBuffer(formatted);
        },
        [setBufferValue]
    );

    const bufferCommitTimeoutRef = useRef(null);

    const clearBufferCommitTimeout = useCallback(() => {
        if (bufferCommitTimeoutRef.current) {
            clearTimeout(bufferCommitTimeoutRef.current);
            bufferCommitTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            clearBufferCommitTimeout();
        };
    }, [clearBufferCommitTimeout]);

    const prevIsBufferActiveRef = useRef(isBufferActive);
    useEffect(() => {
        const prev = prevIsBufferActiveRef.current;

        if (prev && !isBufferActive) {
            clearBufferCommitTimeout();
            setLocalBuffer('');
            setBufferValue(0);
        }

        prevIsBufferActiveRef.current = isBufferActive;
    }, [isBufferActive, clearBufferCommitTimeout, setBufferValue]);

    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const currentTool = activeTool ? toolDetails[activeTool] : null;

    // -------------------------
    // ✅ Keys de todos los autocompletes
    // -------------------------
    const allAutocompleteKeys = useMemo(() => {
        const keys = []
            .concat((dynamicFilters || []).map((f) => f.value))
            .concat((simpleFiltersConfig || []).map((f) => f.value));
        return Array.from(new Set(keys.filter(Boolean)));
    }, [dynamicFilters, simpleFiltersConfig]);

    // -------------------------
    // ✅ PaperComponent estable por key (CRÍTICO: evita cierres raros del dropdown)
    // -------------------------
    const paperComponentMap = useMemo(() => {
        const map = {};

        allAutocompleteKeys.forEach((filterKey) => {
            map[filterKey] = React.forwardRef(function SearchablePaper(paperProps, forwardedRef) {
                const q = dropdownSearchRef.current?.[filterKey] || '';

                const setRef = (node) => {
                    if (node) paperRefs.current[filterKey] = node;

                    if (typeof forwardedRef === 'function') forwardedRef(node);
                    else if (forwardedRef && typeof forwardedRef === 'object') forwardedRef.current = node;
                };

                return (
                    <Paper {...paperProps} ref={setRef}>
                        <Box
                            sx={{
                                p: 0.75,
                                pb: 0.5,
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                                backgroundColor: 'background.paper',
                                borderBottom: '1px solid rgba(0,0,0,0.08)',
                            }}
                            onMouseDownCapture={() => {
                                ignoreCloseRef.current[filterKey] = true;
                                requestAnimationFrame(() => {
                                    delete ignoreCloseRef.current[filterKey];
                                });
                                setOpenForKey(filterKey, true);
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpenForKey(filterKey, true);
                            }}
                        >
                            <TextField
                                size="small"
                                fullWidth
                                value={q}
                                onChange={(e) => setDropdownSearchValue(filterKey, e.target.value)}
                                placeholder="Buscar en la lista…"
                                variant="outlined"
                                inputProps={{ 'aria-label': 'Buscar opciones' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                onKeyDownCapture={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') e.preventDefault();
                                    if (e.key === 'Escape') e.preventDefault();
                                }}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') e.preventDefault();
                                    if (e.key === 'Escape') e.preventDefault();
                                }}
                                onMouseDownCapture={() => {
                                    ignoreCloseRef.current[filterKey] = true;
                                    requestAnimationFrame(() => {
                                        delete ignoreCloseRef.current[filterKey];
                                    });
                                    setOpenForKey(filterKey, true);
                                }}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    setOpenForKey(filterKey, true);
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenForKey(filterKey, true);
                                }}
                            />
                        </Box>

                        {paperProps.children}
                    </Paper>
                );
            });
        });

        return map;
    }, [allAutocompleteKeys, setDropdownSearchValue, setOpenForKey]);

    // -------------------------
    // ✅ filterOptions estable por key (usa ref)
    // -------------------------
    const filterOptionsMap = useMemo(() => {
        const map = {};
        allAutocompleteKeys.forEach((filterKey) => {
            map[filterKey] = (options, state) => {
                const internalQ = dropdownSearchRef.current?.[filterKey] || '';
                const inputQ = state?.inputValue || '';
                const combined = `${inputQ} ${internalQ}`.trim();
                return filterStringOptions(options, combined);
            };
        });
        return map;
    }, [allAutocompleteKeys]);

    // -------------------------
    // ✅ Open/Close handlers (NO cerrar al clickear el buscador interno)
    // -------------------------
    const handleAutocompleteOpen = useCallback(
        (filterKey) => () => {
            setOpenForKey(filterKey, true);
        },
        [setOpenForKey]
    );

    const handleAutocompleteClose = useCallback(
        (filterKey) => (event, reason) => {
            if (reason === 'blur' || reason === 'toggleInput') {
                if (ignoreCloseRef.current?.[filterKey]) {
                    setOpenForKey(filterKey, true);
                    return;
                }

                const paperEl = paperRefs.current?.[filterKey];
                const related = event?.relatedTarget;
                if (paperEl && related && paperEl.contains(related)) {
                    setOpenForKey(filterKey, true);
                    return;
                }
            }

            if (reason === 'selectOption') {
                closeForKey(filterKey);
                clearDropdownSearchValue(filterKey);
                return;
            }

            closeForKey(filterKey);
            clearDropdownSearchValue(filterKey);
        },
        [setOpenForKey, closeForKey, clearDropdownSearchValue]
    );

    // -------------------------
    // ✅ FIX: Click-Away real (captura)
    // -------------------------
    useEffect(() => {
        const onPointerDownCapture = (event) => {
            const current = openByKeyRef.current || {};
            const openKey = Object.keys(current)[0];
            if (!openKey) return;

            const target = event?.target;
            if (!target) return;

            if (ignoreCloseRef.current?.[openKey]) return;

            const paperEl = paperRefs.current?.[openKey];
            const fieldRootEl = fieldRootRefs.current?.[openKey];
            const anchorEl2 = anchorRefs.current?.[openKey];

            if (paperEl && paperEl.contains(target)) return;
            if (fieldRootEl && fieldRootEl.contains(target)) return;
            if (anchorEl2 && anchorEl2.contains(target)) return;

            closeForKey(openKey);
            clearDropdownSearchValue(openKey);
        };

        document.addEventListener('mousedown', onPointerDownCapture, true);
        document.addEventListener('touchstart', onPointerDownCapture, true);

        return () => {
            document.removeEventListener('mousedown', onPointerDownCapture, true);
            document.removeEventListener('touchstart', onPointerDownCapture, true);
        };
    }, [closeForKey, clearDropdownSearchValue]);

    return (
        <FilterBar
            position="static"
            isSidebarOpen={isSidebarOpen}
            isDashboardIndicators={isDashboardIndicators}
            isSmallScreen={isSmallScreen}
        >
            <Toolbar
                variant="dense"
                disableGutters
                sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                {/* Filtros a la izquierda */}
                <Box display="flex" alignItems="center" flexWrap="nowrap" gap={0.5} sx={{ overflowX: 'auto' }}>
                    <IconButton edge="start" color="inherit" aria-label="filter" size="small">
                        <FilterListIcon />
                    </IconButton>

                    {/* ✅ DashboardIndicators: SIN cambiar lógica (single select) + buscador interno estable */}
                    {isDashboardIndicators ? (
                        <>
                            {onDateChange && (
                                <LocalizationProvider dateAdapter={AdapterMoment} locale="es">
                                    <DatePicker
                                        label={isSmallScreen ? 'Inicio' : 'Fecha Inicio'}
                                        value={startDate}
                                        onChange={handleStartDateChange}
                                        maxDate={endDate}
                                        disableFuture
                                        inputFormat="DD/MM/YYYY"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                sx={{
                                                    marginRight: 1,
                                                    minWidth: isSmallScreen ? 70 : 100,
                                                    maxWidth: isSmallScreen ? 100 : 140,
                                                    height: 36,
                                                    '& .MuiInputBase-input': {
                                                        height: '1.25em',
                                                        fontSize: isSmallScreen ? '0.7rem' : '0.8rem',
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                    <DatePicker
                                        label={isSmallScreen ? 'Fin' : 'Fecha Fin'}
                                        value={endDate}
                                        onChange={handleEndDateChange}
                                        minDate={startDate}
                                        disableFuture
                                        inputFormat="DD/MM/YYYY"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                sx={{
                                                    marginRight: 1,
                                                    minWidth: isSmallScreen ? 70 : 100,
                                                    maxWidth: isSmallScreen ? 100 : 140,
                                                    height: 36,
                                                    '& .MuiInputBase-input': {
                                                        height: '1.25em',
                                                        fontSize: isSmallScreen ? '0.7rem' : '0.8rem',
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            )}

                            {dynamicFilters.map((filter) => {
                                const { options, loading } = getOptionsForFilter(filter.value);
                                const filterValue = selectedFilters[filter.value] || null;
                                const isLargeDataset = (options || []).length > 100;
                                const PaperCmp = paperComponentMap[filter.value] || Paper;
                                const filterFn = filterOptionsMap[filter.value];

                                return (
                                    <Autocomplete
                                        key={filter.value}
                                        open={!!openByKey[filter.value]}
                                        onOpen={handleAutocompleteOpen(filter.value)}
                                        onClose={handleAutocompleteClose(filter.value)}
                                        options={uniqueSorted(options || [])}
                                        getOptionLabel={(option) => option}
                                        loading={loading}
                                        value={filterValue}
                                        onChange={(event, newValue) => {
                                            handleFilterChange(filter.value, newValue);
                                        }}
                                        isOptionEqualToValue={(option, value) => option === value}
                                        filterOptions={filterFn}
                                        PaperComponent={PaperCmp}
                                        renderInput={(params) => {
                                            const { ref: autoRef, ...restInputProps } = params.InputProps;

                                            const setAnchorRef = (node) => {
                                                if (node) anchorRefs.current[filter.value] = node;

                                                if (typeof autoRef === 'function') autoRef(node);
                                                else if (autoRef && typeof autoRef === 'object') autoRef.current = node;
                                            };

                                            return (
                                                <TextField
                                                    {...params}
                                                    ref={(node) => {
                                                        if (node) fieldRootRefs.current[filter.value] = node;
                                                    }}
                                                    InputProps={{
                                                        ...restInputProps,
                                                        ref: setAnchorRef,
                                                    }}
                                                    label={isSmallScreen ? filter.label.substring(0, 10) : filter.label}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        marginRight: 0.5,
                                                        minWidth: isSmallScreen ? 70 : 100,
                                                        maxWidth: isSmallScreen ? 100 : 140,
                                                        height: 36,
                                                        '& .MuiInputBase-input': {
                                                            height: '1.25em',
                                                            fontSize: isSmallScreen ? '0.7rem' : '0.8rem',
                                                        },
                                                    }}
                                                />
                                            );
                                        }}
                                        renderOption={(props, option) => (
                                            <Box
                                                component="li"
                                                {...props}
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '200px',
                                                }}
                                            >
                                                {option}
                                            </Box>
                                        )}
                                        ListboxComponent={isLargeDataset ? ListboxComponent : undefined}
                                        sx={{
                                            marginRight: 0.5,
                                            minWidth: isSmallScreen ? 70 : 100,
                                            maxWidth: isSmallScreen ? 100 : 140,
                                        }}
                                    />
                                );
                            })}
                        </>
                    ) : (
                        /* ✅ NO DashboardIndicators: SOLO 4 filtros (multi-select) + buscador interno */
                        <>
                            {simpleFiltersConfig.map((filter) => {
                                const { options, loading } = getOptionsForSimpleFilter(filter);
                                const value = selectedSimpleFilters[filter.value] || [];
                                const isLargeDataset = (options || []).length > 100;
                                const PaperCmp = paperComponentMap[filter.value] || Paper;
                                const filterFn = filterOptionsMap[filter.value];

                                return (
                                    <Autocomplete
                                        key={`${filter.value}-${simpleResetNonce}`}
                                        multiple
                                        disableCloseOnSelect
                                        limitTags={isSmallScreen ? 1 : 2}
                                        renderTags={renderCompactTags}
                                        open={!!openByKey[filter.value]}
                                        onOpen={handleAutocompleteOpen(filter.value)}
                                        onClose={handleAutocompleteClose(filter.value)}
                                        options={uniqueSorted(options || [])}
                                        getOptionLabel={(option) => option}
                                        loading={loading}
                                        value={value}
                                        onChange={(event, newValue) => {
                                            handleSimpleFilterChange(filter.value, newValue);
                                        }}
                                        isOptionEqualToValue={(option, v) => option === v}
                                        filterOptions={filterFn}
                                        PaperComponent={PaperCmp}
                                        sx={{
                                            '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                                            marginRight: 0.5,
                                            minWidth: isSmallScreen ? 70 : 110,
                                            maxWidth: isSmallScreen ? 110 : 160,
                                        }}
                                        renderInput={(params) => {
                                            const { ref: autoRef, ...restInputProps } = params.InputProps;

                                            const setAnchorRef = (node) => {
                                                if (node) anchorRefs.current[filter.value] = node;

                                                if (typeof autoRef === 'function') autoRef(node);
                                                else if (autoRef && typeof autoRef === 'object') autoRef.current = node;
                                            };

                                            return (
                                                <TextField
                                                    {...params}
                                                    ref={(node) => {
                                                        if (node) fieldRootRefs.current[filter.value] = node;
                                                    }}
                                                    InputProps={{
                                                        ...restInputProps,
                                                        ref: setAnchorRef,
                                                    }}
                                                    label={isSmallScreen ? String(filter.label).substring(0, 10) : filter.label}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        marginRight: 0.5,
                                                        minWidth: isSmallScreen ? 70 : 110,
                                                        maxWidth: isSmallScreen ? 110 : 160,
                                                        height: 36,
                                                        '& .MuiInputBase-input': {
                                                            height: '1.25em',
                                                            fontSize: isSmallScreen ? '0.7rem' : '0.8rem',
                                                        },
                                                    }}
                                                />
                                            );
                                        }}
                                        renderOption={(props, option) => (
                                            <Box
                                                component="li"
                                                {...props}
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '200px',
                                                }}
                                            >
                                                {option}
                                            </Box>
                                        )}
                                        ListboxComponent={isLargeDataset ? ListboxComponent : undefined}
                                    />
                                );
                            })}
                        </>
                    )}
                </Box>

                {/* Botones y herramientas a la derecha */}
                <Box display="flex" alignItems="center" flexWrap="nowrap" gap={0.5}>
                    {/* Botón Filtrar */}
                    {onFilterClick && (
                        <Button
                            variant="contained"
                            onClick={handleFilterButtonClick}
                            sx={{
                                marginRight: 0.5,
                                backgroundColor: 'var(--primary-color)',
                                '&:hover': { backgroundColor: 'var(--primary-color-dark)' },
                            }}
                            size={isSmallScreen ? 'small' : 'medium'}
                            disabled={isGeneratingReport}
                        >
                            Filtrar
                        </Button>
                    )}

                    {/* Botón para generar reporte */}
                    {onGenerateReport && (
                        <Tooltip title="Generar Reporte">
                            <IconButton
                                onClick={onGenerateReport}
                                sx={{ color: '#A9A9A9', marginLeft: '5px' }}
                                disabled={isGeneratingReport}
                                size="small"
                            >
                                <FaFilePdf size={20} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {!isDashboardIndicators && <StyledDivider orientation="vertical" flexItem />}

                    {/* Botón para eliminar filtros de lote/finca solo si NO es DashboardIndicators */}
                    {!isDashboardIndicators && (
                        <Tooltip title="Limpiar filtros de lote y finca">
                            <IconButton
                                onClick={handleClearSimpleFilters}
                                sx={{ color: 'var(--primary-color)', marginLeft: '5px' }}
                                size="small"
                            >
                                <FaTrash size={18} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {!isDashboardIndicators && (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={
                                    selectedAnalysisType === 'COSECHA_MECANICA'
                                        ? <FaRuler />
                                        : currentTool
                                            ? currentTool.icon
                                            : <BuildIcon />
                                }
                                endIcon={<ArrowDropDownIcon />}
                                onClick={handleMenuOpen}
                                disabled={!(isAerialApplications || isCosechaMecanica || isDeteccionMaleza)}
                                sx={{
                                    textTransform: 'none',
                                    borderColor:
                                        isAerialApplications || isCosechaMecanica || isDeteccionMaleza
                                            ? 'var(--primary-color)'
                                            : 'rgba(0, 0, 0, 0.23)',
                                    color:
                                        isAerialApplications || isCosechaMecanica || isDeteccionMaleza
                                            ? 'var(--primary-color)'
                                            : 'rgba(0, 0, 0, 0.6)',
                                    padding: '6px 8px',
                                    fontSize: '0.8rem',
                                }}
                            >
                                {isCosechaMecanica
                                    ? 'Cosecha Mecánica'
                                    : isDeteccionMaleza
                                        ? 'Detección Maleza'
                                        : currentTool
                                            ? currentTool.label
                                            : 'Aplicaciones Aéreas'}
                            </Button>

                            <ToolsMenu
                                anchorEl={anchorEl}
                                open={openMenu}
                                onClose={handleMenuClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                disableScrollLock
                                disableAutoFocusItem
                                keepMounted
                                disableEnforceFocus
                                disableAutoFocus
                                disableRestoreFocus
                            >
                                {isCosechaMecanica ? (
                                    <Box display="flex" flexDirection="column" gap={0.5} sx={{ p: 1 }}>
                                        <ToolButton
                                            onClick={() => {
                                                handleMeasurement?.();
                                                handleMenuClose();
                                            }}
                                            active={activeTool === 'measurement'}
                                        >
                                            <FaRuler className="icon" />
                                            Medición
                                        </ToolButton>
                                    </Box>
                                ) : (
                                    <Box display="flex" flexDirection="column" gap={0.5} sx={{ p: 1 }}>
                                        <ToolButton
                                            onClick={() => {
                                                handleCutLine?.();
                                                handleMenuClose();
                                            }}
                                            active={activeTool === 'cut'}
                                        >
                                            <FaCut className="icon" />
                                            Cortar línea
                                        </ToolButton>

                                        <ToolButton
                                            onClick={() => {
                                                handleDrawLine?.();
                                                handleMenuClose();
                                            }}
                                            active={activeTool === 'draw'}
                                        >
                                            <FaDrawPolygon className="icon" />
                                            Dibujar línea
                                        </ToolButton>

                                        <ToolButton
                                            onClick={() => {
                                                handleDeleteLine?.();
                                                handleMenuClose();
                                            }}
                                            active={activeTool === 'delete'}
                                        >
                                            <FaTrash className="icon" />
                                            Borrar líneas
                                        </ToolButton>

                                        <ToolButton
                                            onClick={() => {
                                                handleMeasurement?.();
                                                handleMenuClose();
                                            }}
                                            active={activeTool === 'measurement'}
                                        >
                                            <FaRuler className="icon" />
                                            Medición
                                        </ToolButton>

                                        <ToolButton
                                            onClick={() => {
                                                clearBufferCommitTimeout();
                                                setLocalBuffer('');
                                                setBufferValue(0);
                                                handleToggleBuffer?.();
                                            }}
                                            active={isBufferActive}
                                        >
                                            <FaBuffer className="icon" />
                                            Buffer de línea
                                        </ToolButton>

                                        <ToolButton
                                            onClick={() => {
                                                handleUndo?.();
                                                handleMenuClose();
                                            }}
                                            disabled={!isAerialApplications}
                                        >
                                            <FaUndo className="icon" />
                                            Deshacer
                                        </ToolButton>

                                        <ToolButton
                                            onClick={() => {
                                                handleStretchLine?.();
                                                handleMenuClose();
                                            }}
                                            active={activeTool === 'stretch'}
                                        >
                                            <FaArrowsAltH className="icon" />
                                            Estirar línea
                                        </ToolButton>

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={!!showUnfilteredLines}
                                                    onChange={toggleUnfilteredLines}
                                                    color="primary"
                                                    disabled={!isAerialApplications}
                                                />
                                            }
                                            label="Ruta de vuelo"
                                            sx={{ ml: 0, pl: 0 }}
                                        />

                                        {isBufferActive && (
                                            <TextField
                                                label="Ancho (m)"
                                                placeholder="Ej: 25"
                                                type="text"
                                                value={localBuffer}
                                                onChange={(e) => {
                                                    const v = e.target.value.replace(',', '.');
                                                    if (!/^\d*\.?\d*$/.test(v)) return;

                                                    setLocalBuffer(v);

                                                    clearBufferCommitTimeout();
                                                    bufferCommitTimeoutRef.current = setTimeout(() => {
                                                        commitBufferValue(v, { syncText: false });
                                                    }, 450);
                                                }}
                                                onBlur={() => {
                                                    clearBufferCommitTimeout();
                                                    commitBufferValue(localBuffer, { syncText: true });
                                                }}
                                                onKeyDown={(e) => {
                                                    e.stopPropagation();

                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        clearBufferCommitTimeout();
                                                        commitBufferValue(localBuffer, { syncText: true });
                                                        return;
                                                    }

                                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                        e.preventDefault();
                                                        clearBufferCommitTimeout();

                                                        const cur = Number(String(localBuffer || '').replace(',', '.'));
                                                        const base = Number.isFinite(cur)
                                                            ? cur
                                                            : Number.isFinite(Number(bufferValue))
                                                                ? Number(bufferValue)
                                                                : 0;

                                                        const delta = e.shiftKey ? 10 : e.altKey ? 0.1 : 1;

                                                        const next = e.key === 'ArrowUp' ? base + delta : base - delta;
                                                        const clamped = Math.max(0, Math.min(next, 1000));
                                                        const formatted = Number.isInteger(clamped)
                                                            ? String(clamped)
                                                            : String(Number(clamped.toFixed(2)));

                                                        setLocalBuffer(clamped > 0 ? formatted : '');
                                                        commitBufferValue(clamped > 0 ? formatted : '', { syncText: false });
                                                    }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onWheel={(e) => e.stopPropagation()}
                                                inputProps={{
                                                    inputMode: 'decimal',
                                                    pattern: '[0-9]*[.,]?[0-9]*',
                                                    min: 0,
                                                    step: '0.1',
                                                }}
                                                variant="outlined"
                                                size="small"
                                                sx={{ width: '100%', mt: 0.5, fontSize: '0.8rem' }}
                                                disabled={!isAerialApplications}
                                            />
                                        )}
                                    </Box>
                                )}
                            </ToolsMenu>
                        </>
                    )}
                </Box>
            </Toolbar>
        </FilterBar>
    );
};

export default FilterToolbar;
